import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { Bell, Search, Trash2, Send, User, Filter, CheckCircle, AlertCircle, Info, RefreshCw, Eye } from 'lucide-react';

const NotificationCenter = ({ onStatsUpdate }) => {
  const [notifications, setNotifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    type: 'all',
    is_read: 'all',
    recipient: 'all'
  });
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedNotifications, setSelectedNotifications] = useState([]);

  // Notification type options
  const notificationTypes = [
    { value: 'appointment', label: 'Rendez-vous', icon: CheckCircle, color: 'bg-blue-100 text-blue-800' },
    { value: 'property_update', label: 'Mise à jour propriété', icon: AlertCircle, color: 'bg-green-100 text-green-800' },
    { value: 'document_ready', label: 'Document prêt', icon: Info, color: 'bg-orange-100 text-orange-800' },
    { value: 'general', label: 'Général', icon: Bell, color: 'bg-gray-100 text-gray-800' }
  ];

  // Fetch notifications with user details
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      let query = supabase?.from('notifications')?.select(`
        *,
        recipient:user_profiles!recipient_id(id, full_name, email, role)
      `)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.type !== 'all') {
        query = query?.eq('type', filters?.type);
      }
      if (filters?.is_read !== 'all') {
        query = query?.eq('is_read', filters?.is_read === 'read');
      }
      if (filters?.recipient !== 'all') {
        query = query?.eq('recipient_id', filters?.recipient);
      }

      const { data, error } = await query;
      if (error) throw error;

      setNotifications(data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      alert('Erreur lors du chargement des notifications');
    } finally {
      setLoading(false);
    }
  };

  // Fetch users for filtering and sending notifications
  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('id, full_name, email, role')?.eq('is_active', true)?.order('full_name');
      if (error) throw error;

      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchUsers();
  }, [filters]);

  // Filter notifications based on search term
  const filteredNotifications = notifications?.filter(notification => 
    notification?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    notification?.message?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    notification?.recipient?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  // Handle sending notification
  const handleSendNotification = async (notificationData) => {
    try {
      const { error } = await supabase?.from('notifications')?.insert([notificationData]);
      if (error) throw error;

      setShowSendModal(false);
      fetchNotifications();
      onStatsUpdate?.();
    } catch (error) {
      console.error('Error sending notification:', error);
      alert('Erreur lors de l\'envoi de la notification');
    }
  };

  // Handle notification deletion
  const handleDeleteNotification = async (notificationId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette notification ?')) {
      return;
    }

    try {
      const { error } = await supabase?.from('notifications')?.delete()?.eq('id', notificationId);
      if (error) throw error;

      fetchNotifications();
      onStatsUpdate?.();
    } catch (error) {
      console.error('Error deleting notification:', error);
      alert('Erreur lors de la suppression de la notification');
    }
  };

  // Mark as read/unread
  const handleToggleRead = async (notificationId, isRead) => {
    try {
      const { error } = await supabase?.from('notifications')?.update({ is_read: !isRead })?.eq('id', notificationId);
      if (error) throw error;

      fetchNotifications();
    } catch (error) {
      console.error('Error updating read status:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  // Bulk delete selected notifications
  const handleBulkDelete = async () => {
    if (selectedNotifications?.length === 0) return;
    
    if (!window.confirm(`Supprimer ${selectedNotifications?.length} notification(s) sélectionnée(s) ?`)) {
      return;
    }

    try {
      const { error } = await supabase?.from('notifications')?.delete()?.in('id', selectedNotifications);
      if (error) throw error;

      setSelectedNotifications([]);
      fetchNotifications();
      onStatsUpdate?.();
    } catch (error) {
      console.error('Error bulk deleting notifications:', error);
      alert('Erreur lors de la suppression en masse');
    }
  };

  // Get notification type details
  const getNotificationType = (type) => {
    return notificationTypes?.find(t => t?.value === type) || notificationTypes?.find(t => t?.value === 'general');
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now - date;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Aujourd\'hui';
    if (diffDays === 2) return 'Hier';
    if (diffDays <= 7) return `Il y a ${diffDays - 1} jours`;
    return date?.toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)]?.map((_, i) => (
          <div key={i} className="bg-white p-6 rounded-lg shadow-sm animate-pulse">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Bell className="w-5 h-5 mr-2 text-blue-600" />
              Centre de Notifications
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {filteredNotifications?.length} notification(s) trouvée(s)
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher notifications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* Bulk Delete Button */}
            {selectedNotifications?.length > 0 && (
              <button
                onClick={handleBulkDelete}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>Supprimer ({selectedNotifications?.length})</span>
              </button>
            )}
            
            {/* Refresh Button */}
            <button
              onClick={fetchNotifications}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Actualiser</span>
            </button>
            
            {/* Send Notification Button */}
            <button
              onClick={() => setShowSendModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Send className="w-4 h-4" />
              <span>Nouvelle Notification</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={filters?.type}
              onChange={(e) => setFilters({...filters, type: e?.target?.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tous les types</option>
              {notificationTypes?.map(type => (
                <option key={type?.value} value={type?.value}>{type?.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
            <select
              value={filters?.is_read}
              onChange={(e) => setFilters({...filters, is_read: e?.target?.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tous les statuts</option>
              <option value="unread">Non lues</option>
              <option value="read">Lues</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Destinataire</label>
            <select
              value={filters?.recipient}
              onChange={(e) => setFilters({...filters, recipient: e?.target?.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tous les utilisateurs</option>
              {users?.map(user => (
                <option key={user?.id} value={user?.id}>
                  {user?.full_name} ({user?.role})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-lg shadow-sm">
        {filteredNotifications?.map((notification) => {
          const typeInfo = getNotificationType(notification?.type);
          const TypeIcon = typeInfo?.icon;
          
          return (
            <div key={notification?.id} className={`p-6 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors ${!notification?.is_read ? 'bg-blue-50' : ''}`}>
              <div className="flex items-start space-x-4">
                {/* Selection Checkbox */}
                <div className="pt-1">
                  <input
                    type="checkbox"
                    checked={selectedNotifications?.includes(notification?.id)}
                    onChange={(e) => {
                      if (e?.target?.checked) {
                        setSelectedNotifications([...selectedNotifications, notification?.id]);
                      } else {
                        setSelectedNotifications(selectedNotifications?.filter(id => id !== notification?.id));
                      }
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>

                {/* Notification Icon */}
                <div className={`p-2 rounded-lg ${typeInfo?.color}`}>
                  <TypeIcon className="w-5 h-5" />
                </div>

                {/* Notification Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className={`text-sm font-medium ${!notification?.is_read ? 'text-gray-900' : 'text-gray-700'}`}>
                        {notification?.title}
                      </h4>
                      <p className={`text-sm mt-1 ${!notification?.is_read ? 'text-gray-700' : 'text-gray-500'}`}>
                        {notification?.message}
                      </p>
                      
                      {/* Recipient and Date */}
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                        <div className="flex items-center">
                          <User className="w-3 h-3 mr-1" />
                          <span>{notification?.recipient?.full_name} ({notification?.recipient?.role})</span>
                        </div>
                        <span>•</span>
                        <span>{formatDate(notification?.created_at)}</span>
                      </div>
                    </div>

                    {/* Read Status Indicator */}
                    {!notification?.is_read && (
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleToggleRead(notification?.id, notification?.is_read)}
                    className={`p-2 rounded-lg transition-colors ${
                      notification?.is_read 
                        ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100' :'text-blue-600 hover:text-blue-800 hover:bg-blue-100'
                    }`}
                    title={notification?.is_read ? 'Marquer comme non lu' : 'Marquer comme lu'}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => handleDeleteNotification(notification?.id)}
                    className="p-2 text-red-400 hover:text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                    title="Supprimer la notification"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {filteredNotifications?.length === 0 && (
          <div className="text-center py-12">
            <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Aucune notification trouvée</p>
            <button
              onClick={() => setShowSendModal(true)}
              className="mt-4 flex items-center space-x-2 mx-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Send className="w-4 h-4" />
              <span>Envoyer une notification</span>
            </button>
          </div>
        )}
      </div>

      {/* Send Notification Modal */}
      {showSendModal && (
        <SendNotificationModal
          users={users}
          notificationTypes={notificationTypes}
          onSend={handleSendNotification}
          onClose={() => setShowSendModal(false)}
        />
      )}
    </div>
  );
};

// Send Notification Modal Component
const SendNotificationModal = ({ users, notificationTypes, onSend, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'general',
    recipient_id: '',
    send_to_all: false
  });

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (formData?.send_to_all) {
      // Send to all active users
      users?.forEach(user => {
        onSend({
          ...formData,
          recipient_id: user?.id,
          send_to_all: undefined
        });
      });
    } else {
      onSend(formData);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6 flex items-center">
                    <Send className="w-5 h-5 mr-2 text-blue-600" />
                    Envoyer une Notification
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Titre *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData?.title}
                        onChange={(e) => setFormData({...formData, title: e?.target?.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Titre de la notification..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Message *
                      </label>
                      <textarea
                        required
                        rows={4}
                        value={formData?.message}
                        onChange={(e) => setFormData({...formData, message: e?.target?.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Contenu de la notification..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                      </label>
                      <select
                        value={formData?.type}
                        onChange={(e) => setFormData({...formData, type: e?.target?.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {notificationTypes?.map(type => (
                          <option key={type?.value} value={type?.value}>{type?.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center mb-4">
                      <input
                        type="checkbox"
                        id="send_to_all"
                        checked={formData?.send_to_all}
                        onChange={(e) => setFormData({...formData, send_to_all: e?.target?.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="send_to_all" className="ml-2 block text-sm text-gray-900">
                        Envoyer à tous les utilisateurs
                      </label>
                    </div>

                    {!formData?.send_to_all && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Destinataire *
                        </label>
                        <select
                          required={!formData?.send_to_all}
                          value={formData?.recipient_id}
                          onChange={(e) => setFormData({...formData, recipient_id: e?.target?.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Sélectionner un utilisateur...</option>
                          {users?.map(user => (
                            <option key={user?.id} value={user?.id}>
                              {user?.full_name} ({user?.role}) - {user?.email}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                <Send className="w-4 h-4 mr-2" />
                Envoyer
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;